import React, { useState } from "react";
import { Phone, Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND, SERVICES, QUOTE_SERVICES } from "@/site.config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm({ id = "contact" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: QUOTE_SERVICES[0],
    message: "",
    website: "", // honeypot — must remain empty
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.message.trim()
    ) {
      toast.error("Merci de remplir tous les champs.");
      return;
    }
    try {
      setStatus("loading");
      await axios.post(`${API}/quotes`, form);
      setStatus("success");
      toast.success("Demande envoyée. Nous vous recontactons rapidement.");
      setForm({
        name: "",
        email: "",
        phone: "",
        service: QUOTE_SERVICES[0],
        message: "",
        website: "",
      });
    } catch (err) {
      console.error(err);
      setStatus("error");
      toast.error(
        err?.response?.status === 429
          ? "Trop de demandes. Merci de patienter quelques minutes."
          : err?.response?.data?.detail ||
            "Une erreur est survenue. Veuillez réessayer."
      );
    }
  };

  return (
    <section
      id={id}
      data-testid="contact-section"
      className="relative py-24 sm:py-32 px-5 sm:px-8"
    >
      <div className="absolute inset-0 -z-10 bg-radial-fade" />
      <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-2">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#9DEB30] mb-3">
            · Contact
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            Demandez votre <span className="text-[#9DEB30]">devis gratuit.</span>
          </h2>
          <p className="mt-5 text-zinc-400 max-w-md">
            Vous avez un besoin en nettoyage technique ou en sécurisation de vos
            installations ? Notre équipe vous accompagne avec des solutions adaptées.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={BRAND.phoneHref}
              data-testid="contact-phone"
              className="group flex items-center gap-4 p-5 rounded-2xl border border-white/10 hover:border-[#9DEB30]/40 transition"
            >
              <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#9DEB30]/10 border border-[#9DEB30]/30 text-[#9DEB30]">
                <Phone size={18} />
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                  Téléphone
                </div>
                <div className="font-display text-lg font-bold">{BRAND.phone}</div>
              </div>
            </a>

            <a
              href={BRAND.emailHref}
              data-testid="contact-email"
              className="group flex items-center gap-4 p-5 rounded-2xl border border-white/10 hover:border-[#9DEB30]/40 transition"
            >
              <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#9DEB30]/10 border border-[#9DEB30]/30 text-[#9DEB30]">
                <Mail size={18} />
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                  Email
                </div>
                <div className="font-display text-lg font-bold">{BRAND.email}</div>
              </div>
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="quote-form"
          className="lg:col-span-3 p-7 sm:p-10 rounded-2xl border border-white/10 bg-[#101010] neon-glow-soft"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Nom complet" name="name" value={form.name} onChange={onChange} placeholder="Jean Dupont" testid="quote-name" />
            <Field label="Téléphone" name="phone" value={form.phone} onChange={onChange} placeholder="0696 ..." testid="quote-phone" />
            <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} placeholder="vous@email.com" testid="quote-email" />
            <SelectField label="Service" name="service" value={form.service} onChange={onChange} options={QUOTE_SERVICES} testid="quote-service" />
          </div>

          <div className="mt-5">
            {/* Honeypot field — hidden from users, visible to bots */}
            <div aria-hidden="true" className="absolute opacity-0 pointer-events-none -z-10 h-0 overflow-hidden">
              <label>
                Site web (laisser vide)
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={onChange}
                />
              </label>
            </div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
              Décrivez votre besoin
            </label>
            <textarea
              data-testid="quote-message"
              name="message"
              value={form.message}
              onChange={onChange}
              rows={5}
              placeholder="Type de bâtiment, surface estimée, urgence éventuelle..."
              className="w-full resize-none px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#9DEB30]/60 focus:ring-2 focus:ring-[#9DEB30]/20 transition"
            />
          </div>

          <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-xs text-zinc-500">
              En envoyant ce formulaire, vous acceptez d'être recontacté par notre équipe.
            </p>
            <button
              type="submit"
              data-testid="quote-submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full btn-neon disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "loading" ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Envoi...
                </>
              ) : status === "success" ? (
                <>
                  <CheckCircle2 size={16} />
                  Envoyé
                </>
              ) : (
                <>
                  Envoyer ma demande
                  <Send size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#9DEB30]/60 focus:ring-2 focus:ring-[#9DEB30]/20 transition"
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
        {label}
      </label>
      <select
        data-testid={testid}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#9DEB30]/60 focus:ring-2 focus:ring-[#9DEB30]/20 transition appearance-none"
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-[#101010]">
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
